import ExcelJS from 'exceljs';
import env from '../../config/env';
import { convertAllDates, netHST, numToCurrency } from '../../utils/Functions';

// Distribution transmittal sheet - one per distributed deal, laid out like the developer's transmittal form:
// project logo, deal summary, deposit table, the list of items included, notes, and "Prepared by".
// Columns: A/C are the two narrow CHECK columns, D-L hold the form.

const GREY = 'FF808080';
const HEADER_GREY = 'FFD9D9D9';
const THIN = { style: 'thin' as const, color: { argb: 'FF000000' } };
const ALL_BORDERS = { top: THIN, left: THIN, bottom: THIN, right: THIN };
const FONT = { name: 'Arial', size: 9 };
const SENT_STATUSES = ['received', 'distributed', 'wire'];
// Blank lines under the deposit table for the items list (more are added if there are more items)
const MIN_ITEM_ROWS = 25;

const ordinal = (n: number) => {
  const suffix = n % 10 === 1 && n % 100 !== 11 ? 'st' : n % 10 === 2 && n % 100 !== 12 ? 'nd' : n % 10 === 3 && n % 100 !== 13 ? 'rd' : 'th';
  return `${n}${suffix}`;
};

const formatDate = (value: any) => (value ? convertAllDates(value, 'PP') : '');

// Excel column widths are in character units; at the default font one unit is ~7px plus ~5px of padding
const columnPixels = (width: number) => Math.round(width * 7 + 5);
const sheetWidthPixels = (sheet: ExcelJS.Worksheet) =>
  (sheet.columns || []).reduce((total, column) => total + columnPixels(column.width || 8.43), 0);
// Converts a pixel offset from the left edge into ExcelJS's fractional column position (e.g. 4.5 = halfway into E)
const columnAtPixel = (sheet: ExcelJS.Worksheet, pixels: number) => {
  let remaining = Math.max(0, pixels);
  const columns = sheet.columns || [];
  for (let i = 0; i < columns.length; i++) {
    const width = columnPixels(columns[i].width || 8.43);
    if (remaining < width) return i + remaining / width;
    remaining -= width;
  }
  return columns.length;
};

// Loads the project's logo through the API's /images/projects/:id/logo route; null if there isn't one (or it's not PNG/JPEG)
const loadProjectLogo = async (projectId: string) => {
  try {
    const response = await fetch(`${env.basePath}/images/projects/${projectId}/logo`, { credentials: 'include' });
    if (!response.ok) return null;
    const blob = await response.blob();
    const extension = blob.type.includes('png') ? 'png' : blob.type.includes('jpeg') || blob.type.includes('jpg') ? 'jpeg' : null;
    if (!extension) return null;
    const bitmap = await createImageBitmap(blob);
    return { buffer: await blob.arrayBuffer(), extension: extension as 'png' | 'jpeg', width: bitmap.width, height: bitmap.height };
  } catch (err) {
    console.log(err, 'err');
    return null;
  }
};

export interface ITransmittalDeal {
  suite: string;
  model: string;
  size: number | string;
  saleDate: any;
  firmDate: any;
  purchasers: string[];
  brokerage: string;
  agent: string;
  unitPrice: number;
  totalPrice: number;
  deposits: { name?: string; chequeNumber?: string; amount?: number; chequeDate?: any; chequeType?: string }[];
  items: string[];
}

// Builds the deal fields the sheet needs from a distribution deal and the items selected for it
export const transmittalDealFrom = (deal: any, project: any, items: string[]): ITransmittalDeal => {
  const realtor = deal.realtor && deal.realtor.length ? deal.realtor[0] : null;
  // netHst projects: the unit's base price net of HST, and that plus the deal's options. Otherwise the deal's own prices.
  const optionsTotal = (deal.options || []).reduce((total: number, option: any) => total + (Number(option.amount) || 0), 0);
  const unitPrice = project.netHst ? netHST(deal.unit?.basePrice || 0, project) : deal.basePrice || 0;
  const totalPrice = project.netHst ? unitPrice + optionsTotal : deal.totalPrice || deal.basePrice || 0;
  return {
    suite: deal.unit?.suite || '',
    model: deal.unit?.modelType || '',
    size: deal.unit?.size || '',
    saleDate: deal.signDate || deal.createdAt,
    firmDate: deal.firmDate,
    purchasers: (deal.purchasers || []).map((purchaser: any) => purchaser.fullName),
    brokerage: realtor ? realtor.brokerage || '' : '',
    agent: realtor ? realtor.fullName || '' : deal.tags?.includes('Friends and Family') ? 'Friends and Family' : 'Public',
    unitPrice,
    totalPrice,
    deposits: deal.deposit || [],
    items,
  };
};

export const buildTransmittalWorkbook = async (deal: ITransmittalDeal, project: any, preparedBy: string) => {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(`Suite ${deal.suite}`.slice(0, 31), {
    pageSetup: { paperSize: 1, orientation: 'portrait', fitToPage: true, fitToWidth: 1, fitToHeight: 0, margins: { left: 0.4, right: 0.4, top: 0.4, bottom: 0.4, header: 0.2, footer: 0.2 } },
    views: [{ showGridLines: true }],
  });
  sheet.columns = [
    { width: 4 }, // A  CHECK
    { width: 1.5 }, // B
    { width: 4 }, // C  CHECK
    { width: 13 }, // D  labels / Deposit
    { width: 12 }, // E
    { width: 11 }, // F  Cheque #
    { width: 11 }, // G  Model label
    { width: 13 }, // H  Amount
    { width: 11 }, // I
    { width: 13 }, // J  right-hand labels / Cheque Date
    { width: 13 }, // K  Comments
    { width: 13 }, // L
  ];
  sheet.properties.defaultRowHeight = 15;

  const cell = (address: string, value: any, style: Partial<ExcelJS.Style> = {}) => {
    const c = sheet.getCell(address);
    c.value = value;
    c.font = { ...FONT, ...(style.font || {}) };
    if (style.alignment) c.alignment = style.alignment;
    if (style.fill) c.fill = style.fill;
    if (style.border) c.border = style.border;
    if (style.numFmt) c.numFmt = style.numFmt;
    return c;
  };
  const label = (address: string, text: string) =>
    cell(address, text, { font: { ...FONT, underline: true }, alignment: { horizontal: 'right', vertical: 'middle' } });
  const value = (range: string, v: any, style: Partial<ExcelJS.Style> = {}) => {
    const [start] = range.split(':');
    if (range.includes(':')) sheet.mergeCells(range);
    return cell(start, v, { alignment: { horizontal: 'left', vertical: 'middle' }, ...style });
  };
  const greyBar = (row: number, text = '', options: { white?: boolean; right?: boolean } = {}) => {
    for (let col = 1; col <= 12; col++) {
      sheet.getRow(row).getCell(col).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: GREY } };
    }
    if (text) {
      const address = options.right ? `J${row}` : `D${row}`;
      sheet.mergeCells(options.right ? `J${row}:L${row}` : `D${row}:L${row}`);
      cell(address, text, {
        font: { ...FONT, bold: true, color: { argb: options.white ? 'FFFFFFFF' : 'FF000000' } },
        alignment: { horizontal: options.right ? 'right' : 'left', vertical: 'middle' },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: GREY } },
      });
    }
    sheet.getRow(row).height = 18;
  };

  // Logo (rows 1-5), centred across the sheet, with the project name in bold underneath (row 6)
  const logo = await loadProjectLogo(project._id);
  for (let row = 1; row <= 5; row++) sheet.getRow(row).height = 20;
  if (logo) {
    const imageId = workbook.addImage({ buffer: logo.buffer as any, extension: logo.extension });
    const height = 115;
    const width = Math.round((logo.width / logo.height) * height);
    sheet.addImage(imageId, { tl: { col: columnAtPixel(sheet, (sheetWidthPixels(sheet) - width) / 2), row: 0.15 }, ext: { width, height } });
  }
  value('A6:L6', project.name, { font: { ...FONT, size: 14, bold: true }, alignment: { horizontal: 'center', vertical: 'middle' } });
  sheet.getRow(6).height = 22;

  label('J7', 'Distribution Date:');
  value('K7:L7', formatDate(new Date()));
  greyBar(8);

  // Deal summary
  label('D10', 'Suite #:');
  value('E10:F10', deal.suite);
  label('G10', 'Model:');
  value('H10:I10', deal.model);
  label('J10', 'Size:');
  value('K10', deal.size);
  cell('L10', 'SQFT');
  label('D11', 'Sale Date:');
  value('E11:F11', formatDate(deal.saleDate));
  label('J11', 'Firm Date:');
  value('K11:L11', formatDate(deal.firmDate));
  label('D12', '1st Purchaser:');
  value('E12:I12', deal.purchasers[0] || '');
  label('J12', 'Brokerage:');
  value('K12:L12', deal.brokerage);
  label('D13', '2nd Purchaser:');
  value('E13:I13', deal.purchasers.slice(1).join(', '));
  label('J13', 'Agent:');
  value('K13:L13', deal.agent);
  label('D15', 'Unit Price:');
  value('E15:F15', numToCurrency.format(deal.unitPrice));
  label('D16', 'Total Price:');
  value('E16:F16', numToCurrency.format(deal.totalPrice));

  greyBar(18, 'ITEMS INCLUDED WITH THIS TRANSMITTAL:');

  // Deposit table
  const headerRow = 20;
  cell(`A${headerRow - 1}`, 'CHECK', { font: { ...FONT, size: 6, bold: true } });
  const headers: [string, string][] = [
    [`D${headerRow}:E${headerRow}`, 'Deposit'],
    [`F${headerRow}:G${headerRow}`, 'Cheque #'],
    [`H${headerRow}:I${headerRow}`, 'Amount'],
    [`J${headerRow}`, 'Cheque Date'],
    [`K${headerRow}:L${headerRow}`, 'Comments'],
  ];
  // A bordered table cell spanning `range` (merged), styled on its first cell; the value is set beforehand
  const boxRange = (range: string, style: Partial<ExcelJS.Style>) => {
    const [start, end = start] = range.split(':');
    if (start !== end) sheet.mergeCells(range);
    const first = sheet.getCell(start);
    const last = sheet.getCell(end);
    for (let col = Number(first.col); col <= Number(last.col); col++) sheet.getRow(Number(first.row)).getCell(col).border = ALL_BORDERS;
    first.font = { ...FONT, ...(style.font || {}) };
    if (style.alignment) first.alignment = style.alignment;
    if (style.fill) first.fill = style.fill;
  };
  headers.forEach(([range, text]) => {
    sheet.getCell(range.split(':')[0]).value = text;
    boxRange(range, {
      font: { ...FONT, bold: true },
      alignment: { horizontal: 'center', vertical: 'middle' },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_GREY } },
    });
  });

  const depositRows = Math.max(5, deal.deposits.length);
  for (let i = 0; i < depositRows; i++) {
    const row = headerRow + 1 + i;
    const deposit = deal.deposits[i];
    sheet.getCell(`A${row}`).border = ALL_BORDERS;
    // The deposit's own name (e.g. Occupancy); blank rows and unnamed deposits get 1st/2nd/... Deposit
    sheet.getCell(`D${row}`).value = deposit?.name || `${ordinal(i + 1)} Deposit`;
    boxRange(`D${row}:E${row}`, { font: { ...FONT, bold: true }, alignment: { horizontal: 'center', vertical: 'middle' } });
    sheet.getCell(`F${row}`).value = deposit?.chequeNumber || '';
    boxRange(`F${row}:G${row}`, { alignment: { horizontal: 'center', vertical: 'middle' } });
    sheet.getCell(`H${row}`).value = deposit && deposit.amount !== undefined && deposit.amount !== null ? numToCurrency.format(deposit.amount) : '';
    boxRange(`H${row}:I${row}`, { alignment: { horizontal: 'center', vertical: 'middle' } });
    sheet.getCell(`J${row}`).value = deposit?.chequeDate ? formatDate(deposit.chequeDate) : '';
    boxRange(`J${row}`, { alignment: { horizontal: 'center', vertical: 'middle' } });
    sheet.getCell(`K${row}`).value = deposit && SENT_STATUSES.includes(deposit.chequeType || '') ? 'Sent' : '';
    boxRange(`K${row}:L${row}`, { alignment: { horizontal: 'center', vertical: 'middle' } });
  }

  // Items included: every selected document, mortgage pre-approval, cheque image and ID, one per line
  const itemsStart = headerRow + depositRows + 3;
  cell(`A${itemsStart - 1}`, 'CHECK', { font: { ...FONT, size: 6, bold: true } });
  const itemRows = Math.max(MIN_ITEM_ROWS, deal.items.length);
  for (let i = 0; i < itemRows; i++) {
    const row = itemsStart + i;
    sheet.getCell(`A${row}`).border = ALL_BORDERS;
    sheet.getCell(`C${row}`).border = ALL_BORDERS;
    sheet.mergeCells(`D${row}:L${row}`);
    cell(`D${row}`, deal.items[i] || '', {
      alignment: { horizontal: 'left', vertical: 'middle' },
      border: { top: THIN, bottom: THIN },
    });
  }

  // Notes / Comments
  const notes = itemsStart + itemRows + 1;
  greyBar(notes, 'Notes / Comments', { white: true });
  cell(`D${notes + 1}`, 'Outstanding:');
  cell(`D${notes + 3}`, 'Valid Mortgage');
  cell(`D${notes + 4}`, 'Lawyer Information Form');
  greyBar(notes + 9, `Prepared by: ${preparedBy}`, { white: true, right: true });

  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
};
